import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BottomSheet } from '../base/BottomSheet';
import { getKycLabel, isEkycVerified } from '../../store';
import type { KycStatus } from '../../types';

interface MenuItem {
  id: string;
  label: string;
  subLabel?: string;
  icon: string;
  path?: string;
  requiresEkyc?: boolean;
  actionType?: 'navigate' | 'bottomSheet';
  bottomSheetTitle?: string;
  bottomSheetContent?: string;
}

const HTX_MENU_ITEMS: MenuItem[] = [
  {
    id: 'harvest',
    label: 'Quản lý thu hoạch',
    subLabel: 'Theo dõi sản lượng & chất lượng',
    icon: 'ri-shopping-basket-line',
    path: '/htx/thu-hoach',
  },
  {
    id: 'supply',
    label: 'Quản lý cung ứng',
    subLabel: 'Kho vật tư & dịch vụ HTX',
    icon: 'ri-truck-line',
    path: '/htx/cung-ung',
  },
  {
    id: 'notifications',
    label: 'Thông báo',
    subLabel: 'Tin nhắn & cảnh báo hệ thống',
    icon: 'ri-notification-3-line',
    actionType: 'bottomSheet',
    bottomSheetTitle: 'Thông báo',
    bottomSheetContent: 'Tính năng Thông báo đang được phát triển. Bạn sẽ sớm nhận được thông báo về yêu cầu vay mới, giải ngân, thu nợ đến hạn, tồn kho sắp hết và các cảnh báo quan trọng khác.',
  },
  {
    id: 'htx-profile',
    label: 'Hồ sơ HTX',
    subLabel: 'Thông tin hợp tác xã',
    icon: 'ri-building-line',
    path: '/htx/ho-so-htx',
  },
];

const FARMER_MENU_ITEMS: MenuItem[] = [
  {
    id: 'field-season',
    label: 'Thửa đất & mùa vụ',
    subLabel: 'Quản lý đất canh tác',
    icon: 'ri-landscape-line',
    path: '/nong-ho/ho-so',
  },
  {
    id: 'loan-history',
    label: 'Lịch sử khoản vay',
    subLabel: 'Xem các khoản đã vay',
    icon: 'ri-money-cny-circle-line',
    path: '/nong-ho/vay-von',
    requiresEkyc: true,
  },
  {
    id: 'supply-history',
    label: 'Lịch sử vật tư',
    subLabel: 'Vật tư đã nhận từ HTX',
    icon: 'ri-seedling-line',
    path: '/nong-ho/vat-tu',
  },
  {
    id: 'support',
    label: 'Hỗ trợ',
    subLabel: 'Liên hệ & trợ giúp',
    icon: 'ri-customer-service-2-line',
    actionType: 'bottomSheet',
    bottomSheetTitle: 'Hỗ trợ',
    bottomSheetContent: 'Cần hỗ trợ? Vui lòng liên hệ HTX của bạn qua hotline hoặc đến trực tiếp văn phòng HTX trong giờ hành chính. Đội ngũ Agrida cũng sẵn sàng hỗ trợ bạn qua email: support@agrida.vn.',
  },
  {
    id: 'farmer-profile',
    label: 'Hồ sơ cá nhân',
    subLabel: 'Thông tin cá nhân & eKYC',
    icon: 'ri-user-line',
    path: '/nong-ho/ho-so',
  },
];

interface SlideMenuProps {
  open: boolean;
  onClose: () => void;
  user: {
    displayName: string;
    role: string;
    kycStatus: KycStatus;
    creditScore?: number;
    code?: string;
  };
}

export function SlideMenu({ open, onClose, user }: SlideMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [infoSheetOpen, setInfoSheetOpen] = useState(false);
  const [infoSheetTitle, setInfoSheetTitle] = useState('');
  const [infoSheetContent, setInfoSheetContent] = useState('');
  const [ekycLockSheetOpen, setEkycLockSheetOpen] = useState(false);
  const [ekycLockItemLabel, setEkycLockItemLabel] = useState('');
  const isHTX = user.role === 'Hợp tác xã';
  const items = isHTX ? HTX_MENU_ITEMS : FARMER_MENU_ITEMS;
  const ekycOk = isEkycVerified({ kycStatus: user.kycStatus });

  // Handle open/close with animation
  useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
    } else if (visible) {
      setClosing(true);
      const timer = setTimeout(() => setVisible(false), 250);
      return () => clearTimeout(timer);
    }
  }, [open, visible]);

  // Lock body scroll when menu is visible
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [visible]);

  // ESC to close
  useEffect(() => {
    if (!visible || closing) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [visible, closing, onClose]);

  // Swipe left to close
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (diff < -60) onClose();
  }, [onClose]);

  const handleItemClick = useCallback((item: MenuItem) => {
    if (item.actionType === 'bottomSheet') {
      setInfoSheetTitle(item.bottomSheetTitle || '');
      setInfoSheetContent(item.bottomSheetContent || '');
      setInfoSheetOpen(true);
      return;
    }

    if (item.requiresEkyc && !ekycOk) {
      setEkycLockItemLabel(item.label);
      setEkycLockSheetOpen(true);
      return;
    }

    if (item.path) {
      onClose();
      setTimeout(() => navigate(item.path), 200);
    }
  }, [ekycOk, navigate, onClose]);

  const kycBadge = (() => {
    const status = user.kycStatus || 'registered';
    if (status === 'ekyc_verified') {
      return { bg: 'bg-white/15', text: 'text-white', label: getKycLabel(status), icon: 'ri-shield-check-fill' };
    }
    if (status === 'ekyc_pending') {
      return { bg: 'bg-amber-400/25', text: 'text-amber-50', label: getKycLabel(status), icon: 'ri-time-line' };
    }
    return { bg: 'bg-white/15', text: 'text-white/90', label: getKycLabel(status), icon: 'ri-shield-user-line' };
  })();
  // eKYC is a farmer-only concept today (no equivalent flow exists for an HTX's own
  // identity) — only make this actionable for the farmer role, and only while it
  // isn't already verified, so tapping it never lands on a flow that can't succeed.
  const kycBadgeClickable = !isHTX && user.kycStatus !== 'ekyc_verified';
  const handleKycBadgeClick = useCallback(() => {
    if (!kycBadgeClickable) return;
    onClose();
    setTimeout(() => navigate('/ekyc'), 200);
  }, [kycBadgeClickable, navigate, onClose]);

  if (!visible) return null;

  return (
    <>
      {/* Full-viewport overlay */}
      <div
        className={`fixed inset-0 z-[90] transition-opacity duration-250 ${
          closing ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ pointerEvents: closing ? 'none' : 'auto' }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
        />

        {/* Menu panel - slides from left */}
        <div
          ref={menuRef}
          className={`absolute top-0 left-0 bottom-0 w-[85%] max-w-[340px] bg-white shadow-2xl flex flex-col transition-transform duration-250 ease-out ${
            closing ? '-translate-x-full' : 'translate-x-0'
          }`}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Header */}
          <div className="shrink-0 bg-gradient-to-br from-agrida-600 to-agrida-700 text-white px-5 pt-12 pb-5">
            <div className="flex items-center justify-between">
              <span className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold shrink-0">
                {user.displayName.charAt(0)}
              </span>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors active:scale-95 shrink-0"
                aria-label="Đóng menu"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <h3 className="font-bold text-base leading-tight truncate mt-4">{user.displayName}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-white/70 text-xs">{isHTX ? 'Hợp tác xã' : 'Nông hộ'}</span>
              {user.code && (
                <span className="text-white/50 text-xs">· {user.code}</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              {/* Tappable while unverified: this badge is the only thing in the menu
                  that mentions eKYC, so it is what gets tapped to start it. As a
                  plain <span> it swallowed the tap and read as a dead control. */}
              {kycBadgeClickable ? (
                <button
                  type="button"
                  onClick={handleKycBadgeClick}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors active:scale-95 ${kycBadge.bg} ${kycBadge.text}`}
                >
                  <i className={`${kycBadge.icon} text-xs`} />
                  {kycBadge.label}
                  <i className="ri-arrow-right-s-line text-xs opacity-70" />
                </button>
              ) : (
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${kycBadge.bg} ${kycBadge.text}`}>
                  <i className={`${kycBadge.icon} text-xs`} />
                  {kycBadge.label}
                </span>
              )}
              {user.creditScore && (
                <span className="text-white/70 text-xs">
                  Score: {user.creditScore}
                </span>
              )}
            </div>
          </div>

          {/* Menu items */}
          <div className="flex-1 overflow-y-auto py-2 overscroll-contain">
            {items.map((item) => {
              const isActive = item.path && location.pathname === item.path;
              const isLocked = item.requiresEkyc && !ekycOk;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleItemClick(item)}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors min-h-[48px] ${
                    isActive
                      ? 'bg-agrida-50 text-agrida-700'
                      : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                  } ${isLocked ? 'opacity-50' : ''}`}
                >
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-agrida-100' : 'bg-gray-100'}`}>
                    <i className={`${item.icon} text-lg ${isActive ? 'text-agrida-600' : 'text-gray-500'}`} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium whitespace-nowrap ${isActive ? 'text-agrida-700' : 'text-gray-800'}`}>
                        {item.label}
                      </span>
                      {isLocked && (
                        <span className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-lock-line text-xs text-gray-400" />
                        </span>
                      )}
                    </div>
                    {item.subLabel && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{item.subLabel}</p>
                    )}
                  </div>
                  {item.actionType === 'bottomSheet' && (
                    <span className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className="ri-arrow-right-s-line text-gray-300" />
                    </span>
                  )}
                  {item.path && !isLocked && (
                    <span className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`ri-arrow-right-s-line ${isActive ? 'text-agrida-400' : 'text-gray-300'}`} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-gray-100 py-3 px-5">
            <p className="text-xs text-gray-400 text-center">
              {isHTX ? 'Agrida — Quản lý HTX' : 'Agrida — Nông hộ'}
            </p>
          </div>
        </div>
      </div>

      {/* Info BottomSheet */}
      <BottomSheet
        open={infoSheetOpen}
        onClose={() => setInfoSheetOpen(false)}
        title={infoSheetTitle}
      >
        <div className="py-2">
          <div className="flex items-center justify-center mb-4">
            <span className="w-16 h-16 rounded-full bg-agrida-50 flex items-center justify-center">
              <i className="ri-information-line text-3xl text-agrida-500" />
            </span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed text-center">{infoSheetContent}</p>
        </div>
      </BottomSheet>

      {/* eKYC Lock BottomSheet */}
      <BottomSheet
        open={ekycLockSheetOpen}
        onClose={() => setEkycLockSheetOpen(false)}
        title="Cần xác thực eKYC"
      >
        <div className="py-2">
          <div className="flex items-center justify-center mb-4">
            <span className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center">
              <i className="ri-shield-check-line text-3xl text-orange-500" />
            </span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed text-center mb-2">
            Để sử dụng <strong>{ekycLockItemLabel}</strong>, bạn cần hoàn tất xác thực eKYC.
          </p>
          <p className="text-xs text-gray-400 leading-relaxed text-center mb-4">
            eKYC giúp bảo vệ tài khoản và đảm bảo các giao dịch tài chính an toàn.
          </p>
          <button
            type="button"
            onClick={() => {
              setEkycLockSheetOpen(false);
              onClose();
              setTimeout(() => navigate('/ekyc'), 200);
            }}
            className="w-full py-2.5 bg-agrida-600 text-white rounded-xl text-sm font-medium hover:bg-agrida-700 transition-colors whitespace-nowrap"
          >
            Thực hiện eKYC ngay
          </button>
        </div>
      </BottomSheet>
    </>
  );
}